import { Button, VStack } from '@chakra-ui/react';
import { FC, Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CommentInput } from '@/components/ui/features/lecture/LectureDiscussion/components/CommentInput';
import {
  CommentListItem,
  CommentListItemEmpty,
  CommentListItemFallback,
} from '@/components/ui/features/lecture/LectureDiscussion/components/CommentListItem';
import { CommentMenu } from '@/components/ui/features/lecture/LectureDiscussion/components/CommentMenu';
import { ErrorFallback } from '@/components/ui/shared/overlays/ErrorFallback/ErrorFallback';
import { BugsnagErrorBoundary } from '@/components/ui/shared/utilities/BugsnagErrorBoundary/BugsnagErrorBoundary';
import { queryLectureCommentsInfinite } from '@/fetchers/comments';
import { isLastPageInfinite, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { findModelInPages } from '@/utils/findModelInPages';

interface ICommentRootListProps {
  lectureId: string;
  threadId?: never;
  onItemDelete?: (threadId: string) => void;
}

interface ICommentRepliesListProps {
  lectureId?: never;
  threadId: string;
  onItemDelete?: (threadId: string) => void;
}

export type CommentListProps = ICommentRootListProps | ICommentRepliesListProps;

export const CommentList: FC<CommentListProps> = ({ lectureId, threadId, onItemDelete }) => {
  const { t } = useTranslation();
  const { data, size, setSize, mutate, error } = useResourcesInfinite(
    queryLectureCommentsInfinite({ lectureId, threadId })
  );
  const isEmpty = data?.[0]?.data?.length === 0;
  const isLoading = size > 0 && data && !data[size - 1];
  const isLastPage = isLastPageInfinite(data);
  const isRoot = !threadId;

  const handleNext = useCallback(() => setSize(size + 1), [setSize, size]);

  const handleCommentDelete = useCallback(
    (parentId) => {
      mutate((prevPages) => {
        const matchingComment = findModelInPages(prevPages, parentId);

        matchingComment.metadata.repliesCount--;
        return [...prevPages];
      }, false);
    },
    [mutate]
  );

  const handleRepliesChange = useCallback(
    (parentId) => {
      mutate((prevPages) => {
        const matchingComment = findModelInPages(prevPages, parentId);

        matchingComment.metadata.repliesCount++;
        return [...prevPages];
      }, false);
    },
    [mutate]
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Fragment>
        {Array.from(Array(3).keys()).map((i) => (
          <CommentListItemFallback key={i} isRoot={isRoot} />
        ))}
      </Fragment>
    );
  }

  if (isEmpty) {
    return (
      <CommentListItemEmpty isRoot={isRoot}>
        {lectureId ? t('comments.noComments') : t('comments.replies.noReplies')}
      </CommentListItemEmpty>
    );
  }

  return (
    <VStack spacing={8} align="stretch">
      {data.map((commentResponse) =>
        commentResponse.data.map((comment) => (
          <CommentListItem
            key={comment.id}
            lectureId={lectureId}
            comment={comment}
            isRoot={isRoot}
            menu={<CommentMenu threadId={threadId} comment={comment} lectureId={lectureId} onDelete={onItemDelete} />}
            replies={({ isOpen }) =>
              isOpen && (
                <VStack mt={8} spacing={8} align="stretch">
                  <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
                    <CommentList threadId={comment.id} onItemDelete={handleCommentDelete} />
                  </BugsnagErrorBoundary>
                  <CommentInput threadId={comment.id} onSuccess={handleRepliesChange} />
                </VStack>
              )
            }
          />
        ))
      )}

      {!isLastPage && (
        <Button size="sm" variant="outline" isLoading={isLoading} onClick={handleNext}>
          {t('common.loadMore')}
        </Button>
      )}
    </VStack>
  );
};
